import React, { memo, useRef } from 'react';
import EdgeInsertMenu from '../EdgeInsertMenu';
import NodeConnectionHandles from './components/NodeConnectionHandles';
import DragHandlesGroup from './components/DragHandlesGroup';
import EdgeInsertButtons from './components/EdgeInsertButtons';
import LayerConnectionMenu from './components/LayerConnectionMenu';
import { useHandlePositions } from './hooks/useHandlePositions';
import { useEdgeInteraction } from './hooks/useEdgeInteraction';
import { useConnectionActions } from './hooks/useConnectionActions';
import { useVisibleHandleTasks } from './hooks/useVisibleHandleTasks';
import { useInstantHandleHover } from './hooks/useInstantHandleHover';
import './styles.css';

/**
 * HandlesLayer Component
 *
 * PERFORMANCE ARCHITECTURE:
 * This layer renders zoom-independent UI elements (connection handles, edge insert buttons)
 * OUTSIDE the zoomed canvas.
 *
 * WHY THIS EXISTS:
 * Previously, these elements lived inside the zoomed canvas and used CSS `calc()` with
 * `--inverse-zoom` to counteract the zoom. This caused massive performance issues because
 * every zoom change triggered style recalculations for 80+ elements.
 *
 * HOW IT WORKS:
 * 1. Elements are positioned using screen coordinates (not canvas coordinates)
 * 2. They don't zoom at all - they maintain constant screen size naturally
 * 3. Position updates happen via direct DOM manipulation for performance
 * 4. This layer sits on top of the canvas, using pointer-events: none except on handles
 */
const HandlesLayer = memo(({
  tasks,
  allTasks = [],
  nodePositions,
  nodeDimensions,
  edges,
  hoveredNodeId,
  selectedNodes,
  containerRef,
  pan: initialPan,
  zoom: initialZoom,
  isDragging = false,
  onConnectionDragStart,
  onCreateConnectedNode,
  onInsertMissionBefore,
  onPasteBeforeHost,
  checkClipboard,
  onCreateCampaign,
  onInsertNode,
  onNodeDragStart,
  menuHandles = false,
  instantHover = false,
  isMultiCampaignMode = false,
  layoutSurfaceEpoch = 0,
}) => {
  const layerRef = useRef(null);

  const {
    handlesContainerRef,
    decisionHandlesContainerRef,
    edgeButtonsContainerRef,
    dragHandlesContainerRef,
    panRef,
    zoomRef,
    canvasToScreen,
  } = useHandlePositions({
    containerRef,
    layerRef,
    nodePositions,
    nodeDimensions,
    edges,
    initialPan,
    initialZoom,
    layoutSurfaceEpoch,
  });

  const {
    hoveredEdgeId,
    menuEdge,
    menuPos,
    handleEdgeInsertClick,
    handleSelectNodeType,
    handleCloseMenu,
    handleButtonMouseLeave,
    handleInsertMouseEnter,
  } = useEdgeInteraction({
    containerRef,
    canvasToScreen,
    onInsertNode,
  });

  const {
    connectionMenuTaskId,
    connectionMenuPos,
    connectionMenuBranch,
    connectionMenuMode,
    closeConnectionMenu,
    handleConnectionMouseDown,
    handleDecisionBranchMouseDown,
    handleBeforeMouseDown,
    handleDragHandleMouseDown,
    menuElRef,
  } = useConnectionActions({
    containerRef,
    panRef,
    zoomRef,
    onConnectionDragStart,
    onNodeDragStart,
  });

  const {
    visibleHandleTasks,
    regularTasks,
    milestoneTasks,
    decisionTasks,
    pinnedIds,
  } = useVisibleHandleTasks({
    tasks,
    allTasks,
    hoveredNodeId: instantHover ? null : hoveredNodeId,
    connectionMenuTaskId,
    selectedNodes,
  });

  useInstantHandleHover({
    enabled: instantHover,
    containerRef,
    layerRef,
    pinnedIds,
  });

  const shownTasks = instantHover ? tasks : visibleHandleTasks;
  const shownRegular = instantHover
    ? tasks.filter((task) => task.nodeType !== 'decision' && task.nodeType !== 'milestone' && task.nodeType !== 'note')
    : regularTasks;
  const shownMilestones = instantHover
    ? tasks.filter((task) => task.nodeType === 'milestone')
    : milestoneTasks;
  const shownDecisions = instantHover
    ? tasks.filter((task) => task.nodeType === 'decision')
    : decisionTasks;

  return (
    <div ref={layerRef} className="handles-layer">
      <NodeConnectionHandles
        handlesContainerRef={handlesContainerRef}
        decisionHandlesContainerRef={decisionHandlesContainerRef}
        isDragging={isDragging}
        regularTasks={shownRegular}
        milestoneTasks={shownMilestones}
        decisionTasks={shownDecisions}
        nodePositions={nodePositions}
        nodeDimensions={nodeDimensions}
        canvasToScreen={canvasToScreen}
        onConnectionMouseDown={handleConnectionMouseDown}
        onBeforeMouseDown={handleBeforeMouseDown}
        onDecisionBranchMouseDown={handleDecisionBranchMouseDown}
      />
      <DragHandlesGroup
        containerRef={dragHandlesContainerRef}
        isDragging={isDragging}
        tasks={shownTasks}
        nodePositions={nodePositions}
        nodeDimensions={nodeDimensions}
        canvasToScreen={canvasToScreen}
        menuOnly={menuHandles}
        onMouseDown={handleDragHandleMouseDown}
      />
      <EdgeInsertButtons
        containerRef={edgeButtonsContainerRef}
        flowchartRef={containerRef}
        isDragging={isDragging}
        edges={edges}
        panRef={panRef}
        zoomRef={zoomRef}
        canvasToScreen={canvasToScreen}
        hoveredEdgeId={hoveredEdgeId}
        onClick={handleEdgeInsertClick}
        onMouseEnter={handleInsertMouseEnter}
        onMouseLeave={handleButtonMouseLeave}
      />
      {menuEdge && menuPos && (
        <EdgeInsertMenu
          position={menuPos}
          onSelect={handleSelectNodeType}
          onClose={handleCloseMenu}
        />
      )}
      <LayerConnectionMenu
        taskId={connectionMenuTaskId}
        position={connectionMenuPos}
        mode={connectionMenuMode}
        branch={connectionMenuBranch}
        tasks={tasks}
        isMultiCampaignMode={isMultiCampaignMode}
        checkClipboard={checkClipboard}
        onCreateCampaign={onCreateCampaign}
        onCreateConnectedNode={onCreateConnectedNode}
        onInsertMissionBefore={onInsertMissionBefore}
        onPasteBeforeHost={onPasteBeforeHost}
        onClose={closeConnectionMenu}
        menuRef={menuElRef}
      />
    </div>
  );
});

HandlesLayer.displayName = 'HandlesLayer';

export default HandlesLayer;
